<template>
  <main class="login">
    <section class="alinha-v">
      <div class="margem">
        <p class="logo">Thalamus</p>
        <h2 class="titulo-fluxo">{{ titulo }}</h2>
        <p class="texto-fluxo" v-if="!codigoValido">{{ subtituloCodigo }}</p>
        <p class="texto-fluxo" v-else>{{ subtituloSenha }}</p>

        <fieldset v-if="!codigoValido">
          <div class="linha">
            <label class="icone-conta pos-input"></label>
            <input
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              :disabled="codigoValido"
              placeholder="Código de 6 dígitos"
              v-model="codigo"
              @keyup.enter="validarCodigo"
            />
          </div>
        </fieldset>
        <div class="submit" v-if="!codigoValido">
          <button type="button" class="button" :disabled="loading" @click="validarCodigo">
            <i v-if="!loading" class="fa-solid fa-key" aria-hidden="true"></i>&nbsp;
            <i v-if="loading" class="fas fa-spinner fa-spin"></i>
            <span v-if="!loading">Verificar código</span>
            <span v-else>&nbsp;Verificando...</span>
          </button>
        </div>

        <div v-if="codigoValido">
          <fieldset>
            <div class="linha">
              <label class="icone-senha pos-input"></label>
              <input
                :type="showNewPassword ? 'text' : 'password'"
                ref="newPasswordInput"
                :placeholder="placeholderSenha"
                v-model="new_password"
              />
            </div>
            <div class="linha">
              <label class="icone-senha pos-input"></label>
              <input
                :type="showPasswordConf ? 'text' : 'password'"
                ref="newPasswordConfInput"
                placeholder="Repita a senha"
                v-model="new_password_confirmation"
                @keyup.enter="resetarSenha"
              />
            </div>
          </fieldset>
          <div class="submit">
            <button
              type="button"
              class="button"
              @click="resetarSenha"
              v-if="passwordsFilled && !notSamePasswords && passwordValidation.valid"
              :disabled="loading"
            >
              <i v-if="!loading" class="fa-solid fa-circle-check" aria-hidden="true"></i>
              <i v-if="loading" class="fas fa-spinner fa-spin"></i>&nbsp;
              <span v-if="!loading">{{ textoBotaoSenha }}</span>
              <span v-else>Salvando...</span>
            </button>
          </div>
          <div class="matches" v-if="notSamePasswords" style="color: red; text-align: center">
            <p>As senhas não conferem!</p>
          </div>
          <transition name="hint" appear style="position: fixed">
            <div v-if="passwordValidation.errors.length > 0 && !submitted" class="hints">
              <hr />
              <p style="color: red" v-for="error in passwordValidation.errors" :key="error">
                {{ error }}
              </p>
            </div>
          </transition>
        </div>

        <p class="links-aux">
          <RouterLink
            v-if="!codigoValido"
            :to="{ name: 'EsqueciMinhaSenha', query: { motivo } }"
          >
            Não recebi o código — reenviar
          </RouterLink>
          <RouterLink v-else :to="{ name: 'login' }">Ir para o login</RouterLink>
        </p>
      </div>
    </section>
  </main>
</template>

<script>
import { createToaster } from "@meforma/vue-toaster";
import apiR from "@/services/apiLogin.js";

const toaster = createToaster({
  position: "top-right",
  duration: 4000,
});

export default {
  name: "ValidarTokenComponent",
  data() {
    return {
      codigo: "",
      new_password: "",
      new_password_confirmation: "",
      erroCodigo: false,
      codigoValido: false,
      loading: false,
      submitted: false,
      showNewPassword: false,
      showPasswordConf: false,
      validationState: false,
      rules: [
        { message: "- Mínimo de 1 letra maiúscula", regex: /[A-Z]+/ },
        { message: "- Mínimo de 6 caracteres", regex: /.{6,}/ },
        { message: "- Mínimo de 1 número", regex: /[0-9]+/ },
        { message: "- Mínimo de 1 caractere especial (ex: @ ! , . * -)", regex: /[^a-zA-Z 0-9]+/g },
      ],
    };
  },
  computed: {
    motivo() {
      const q = this.$route.query?.motivo;
      if (q === "primeiro-acesso" || q === "esqueci") return q;
      try {
        const raw = sessionStorage.getItem("thalamus_senha_fluxo");
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed?.motivo === "primeiro-acesso" ? "primeiro-acesso" : "esqueci";
      } catch {
        return "esqueci";
      }
    },
    titulo() {
      return this.motivo === "primeiro-acesso" ? "Criar sua senha" : "Redefinir senha";
    },
    subtituloCodigo() {
      return this.motivo === "primeiro-acesso"
        ? "Digite o código de 6 dígitos enviado ao seu e-mail para liberar a criação da senha."
        : "Digite o código de 6 dígitos enviado ao seu e-mail para continuar.";
    },
    subtituloSenha() {
      return this.motivo === "primeiro-acesso"
        ? "Agora escolha uma senha segura para o seu primeiro acesso."
        : "Agora escolha uma nova senha para a sua conta.";
    },
    placeholderSenha() {
      return this.motivo === "primeiro-acesso" ? "Criar senha" : "Nova senha";
    },
    textoBotaoSenha() {
      return this.motivo === "primeiro-acesso" ? "Salvar senha e continuar" : "Salvar nova senha";
    },
    notSamePasswords() {
      if (this.passwordsFilled) {
        return this.new_password !== this.new_password_confirmation;
      }
      return false;
    },
    passwordsFilled() {
      return this.new_password !== "" && this.new_password_confirmation !== "";
    },
    passwordValidation() {
      const errors = [];
      for (const condition of this.rules) {
        if (!condition.regex.test(this.new_password)) {
          errors.push(condition.message);
        }
      }
      return { valid: errors.length === 0, errors };
    },
  },
  methods: {
    validarCodigo() {
      if (!this.codigo) {
        toaster.show("Por favor, preencha o código do e-mail", { type: "error" });
        return;
      }

      this.loading = true;
      apiR
        .post("validar-codigo", { codigo: this.codigo })
        .then((response) => {
          if (response.data === 0) {
            this.erroCodigo = true;
            this.loading = false;
            toaster.show("Código incorreto ou expirado. Solicite um novo se precisar.", {
              type: "error",
            });
          } else if (response.data === 1) {
            this.erroCodigo = false;
            this.codigoValido = true;
            this.loading = false;
            toaster.show("Código validado! Defina sua senha.", { type: "success" });
          }
        })
        .catch((error) => {
          this.loading = false;
          console.error(error);
          toaster.show("Não foi possível validar o código. Tente novamente.", { type: "error" });
        });
    },
    resetarSenha() {
      if (!this.new_password || !this.new_password_confirmation) {
        toaster.show("Por favor, preencha a senha", { type: "error" });
        return;
      }
      if (this.new_password !== this.new_password_confirmation) {
        return;
      }

      this.loading = true;
      this.validationState = true;

      apiR
        .post("redefinir-senha", {
          codigo: this.codigo,
          new_password: this.new_password,
          new_password_confirmation: this.new_password_confirmation,
        })
        .then(() => {
          this.codigo = "";
          this.new_password = "";
          this.new_password_confirmation = "";
          this.loading = false;
          sessionStorage.removeItem("thalamus_senha_fluxo");
          toaster.show(
            this.motivo === "primeiro-acesso"
              ? "Senha criada! Faça login para entrar."
              : "Senha alterada! Faça login com a nova senha.",
            { type: "success" }
          );
          this.$router.push({ name: "login" });
        })
        .catch((error) => {
          this.loading = false;
          this.validationState = false;
          toaster.show("Erro ao salvar a senha. Tente novamente.", { type: "error" });
          console.error("Erro ao redefinir a senha:", error);
        });
    },
  },
};
</script>

<style scoped>
.titulo-fluxo {
  margin: 0 0 0.35rem;
  font-size: 1.25rem;
  font-weight: 600;
}

.texto-fluxo {
  margin: 0 0 1.25rem;
  font-size: 0.92rem;
  line-height: 1.45;
  color: var(--cor-fonte-fraca, #666);
}

.links-aux {
  margin-top: 1rem;
  text-align: center;
  font-size: 0.9rem;
}

.links-aux a {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
}
</style>
